import { useState, useEffect, useCallback } from "react";
import API from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { PaperPlaneTilt, Warning, CheckCircle, Envelope, EnvelopeOpen } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Bulk Nudge dialog — emails every supplier whose compliance documents have
 * no file behind them. Each supplier gets ONE email per call covering all of
 * their orphaned docs. Idempotent (re-sends overwrite the nudged-at timestamp).
 *
 * Pass ``open={bool}`` + ``onClose``. The dialog fetches its own preview on
 * open (count of suppliers and orphan docs) so the admin sees what they're
 * about to send before confirming.
 */
export default function BulkNudgeReuploadDialog({ open, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await API.get("/suppliers/compliance/orphans-preview");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load preview");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) loadPreview();
    else { setPreview(null); setResult(null); }
  }, [open, loadPreview]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await API.post("/suppliers/compliance/nudge-all-orphans");
      setResult(data);
      toast.success(`Sent ${data.emails_sent} re-upload email${data.emails_sent === 1 ? "" : "s"}`);
      onSent && onSent(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk nudge failed");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !sending && onClose && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-nudge-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo] flex items-center gap-2">
            <Envelope className="h-5 w-5 text-amber-600" weight="duotone" />
            Email all suppliers with missing files
          </DialogTitle>
          <DialogDescription>
            Every supplier whose compliance documents have no file behind them gets a one-click re-upload link by email.
            One email per supplier — covering all their orphaned docs.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading preview…</p>
        ) : result ? (
          /* Result panel */
          <div className="space-y-3" data-testid="bulk-nudge-result">
            <div className="border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/30 rounded-md p-3">
              <div className="flex items-center gap-2 mb-1 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                <CheckCircle size={16} weight="duotone" />
                {result.emails_sent} email{result.emails_sent === 1 ? "" : "s"} sent
              </div>
              <p className="text-xs text-emerald-900 dark:text-emerald-200">
                Suppliers will receive a one-click link valid for 14 days. Once they upload, the file_id is wired back automatically.
              </p>
            </div>
            {(result.suppliers_skipped || []).length > 0 && (
              <div className="border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/30 rounded-md p-3">
                <div className="flex items-center gap-2 mb-1 text-sm font-semibold text-amber-800 dark:text-amber-300">
                  <Warning size={16} weight="duotone" />
                  {result.suppliers_skipped.length} skipped
                </div>
                <ul className="text-xs space-y-0.5 text-amber-900 dark:text-amber-200">
                  {result.suppliers_skipped.map(s => (
                    <li key={s.vendor_id}>
                      <strong>{s.vendor_name || s.vendor_id}</strong> — {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : preview && preview.total_suppliers === 0 ? (
          <div className="text-center py-8" data-testid="bulk-nudge-empty">
            <CheckCircle className="h-12 w-12 mx-auto text-emerald-500" weight="duotone" />
            <p className="mt-3 font-semibold">No orphaned documents</p>
            <p className="text-xs text-muted-foreground mt-1">Every compliance document in your tenant has a file behind it. Nothing to send.</p>
          </div>
        ) : preview ? (
          /* Preview panel */
          <div className="space-y-3" data-testid="bulk-nudge-preview">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Suppliers" value={preview.total_suppliers} testid="bulk-nudge-suppliers-count" />
              <Stat label="Orphan docs" value={preview.total_orphan_docs} testid="bulk-nudge-docs-count" />
              <Stat label="With email" value={preview.suppliers_with_email} testid="bulk-nudge-with-email-count" />
            </div>

            <div className="border border-border rounded-md max-h-56 overflow-y-auto" data-testid="bulk-nudge-supplier-list">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-right">Docs</th>
                    <th className="px-3 py-2 text-left">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.suppliers.map(s => (
                    <tr key={s.vendor_id} className="border-t border-border" data-testid={`bulk-nudge-row-${s.vendor_id}`}>
                      <td className="px-3 py-2 font-medium">{s.vendor_name}</td>
                      <td className="px-3 py-2 text-right">{s.doc_count}</td>
                      <td className="px-3 py-2">
                        {s.has_email ? (
                          <span className="text-emerald-700 dark:text-emerald-400 truncate inline-flex items-center gap-1">
                            <EnvelopeOpen size={11} /> {s.email}
                          </span>
                        ) : (
                          <span className="text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                            <Warning size={11} weight="fill" /> no email
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {preview.suppliers_with_email < preview.total_suppliers && (
              <p className="text-[11px] text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
                <Warning size={11} weight="fill" className="shrink-0 mt-0.5" />
                {preview.total_suppliers - preview.suppliers_with_email} supplier{(preview.total_suppliers - preview.suppliers_with_email) === 1 ? "" : "s"} have no email on file and will be skipped. Add an email under Suppliers → details.
              </p>
            )}
          </div>
        ) : null}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="bulk-nudge-cancel">
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button
              onClick={send}
              disabled={sending || loading || !preview || preview.suppliers_with_email === 0}
              data-testid="bulk-nudge-send"
            >
              <PaperPlaneTilt size={14} className="mr-1" />
              {sending ? "Sending…" : `Send ${preview?.suppliers_with_email || 0} email${preview?.suppliers_with_email === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-md border border-border bg-muted/20 px-2 py-2" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-xl font-bold leading-tight">{value}</p>
    </div>
  );
}
